import React, { useState, useEffect } from "react";

import { Line, Bar } from "react-chartjs-2";
import axios from "axios";
import { END_POINT } from "../../constants/defaultValues";
import AffiliateLayout from "./layout";

// styles imported in Main Layout

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  tooltips: {
    mode: "index",
    intersect: false,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    titleFontColor: "#1e293b",
    bodyFontColor: "#475569",
    borderColor: "#e2e8f0",
    borderWidth: 1,
    shadowOffsetX: 4,
    shadowOffsetY: 4,
    shadowBlur: 16,
    shadowColor: "rgba(0,0,0,0.1)",
  },
  hover: {
    mode: "nearest",
    intersect: true,
  },
  scales: {
    yAxes: [
      {
        ticks: { beginAtZero: true, fontColor: "#94a3b8" },
        gridLines: { color: "#f1f5f9", zeroLineColor: "#f1f5f9" },
      },
    ],
    xAxes: [
      {
        ticks: { fontColor: "#94a3b8" },
        gridLines: { display: false },
      },
    ],
  },
  legend: {
    labels: { fontColor: "#475569", usePointStyle: true, boxWidth: 8 },
    position: "top",
    align: "end",
  },
};

const funnelOptions = {
  ...chartOptions,
  scales: {
    xAxes: [{ ticks: { beginAtZero: true }, gridLines: { display: false } }],
    yAxes: [{ gridLines: { display: false } }],
  },
  legend: { display: false },
};

const KPI_Card = ({ title, value, icon, variant = "primary", subText }) => (
  <div className={`stat-card variant-${variant}`}>
    <div className="card-header">
      <div className="icon-box">{icon}</div>
      {subText && <span className="growth-badge">{subText}</span>}
    </div>
    <div className="card-body">
      <p className="stat-label">{title}</p>
      <h3 className="stat-value">{value}</h3>
    </div>
  </div>
);

const AffiliateDashboard = () => {
  const [stats, setStats] = useState({
    total_clicks: 0,
    total_registrations: 0,
    paid_customers: 0,
    total_commission: 0,
    available_balance: 0,
    chart: { labels: [], clicks: [], registrations: [] },
  });
  const [loading, setLoading] = useState(true);

  // Dynamic Chart Data
  const chartData = {
    labels: stats.chart?.labels || [],
    datasets: [
      {
        label: "Click",
        data: stats.chart?.clicks || [],
        borderColor: "rgba(16, 185, 129, 1)",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        borderWidth: 2,
        pointRadius: 4,
        pointBackgroundColor: "#fff",
        pointBorderColor: "rgba(16, 185, 129, 1)",
        fill: true,
      },
      {
        label: "Đăng ký",
        data: stats.chart?.registrations || [],
        borderColor: "rgba(37, 99, 235, 1)",
        backgroundColor: "transparent",
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 4,
        pointBackgroundColor: "#fff",
        pointBorderColor: "rgba(37, 99, 235, 1)",
        fill: false,
      },
    ],
  };

  const funnelData = {
    labels: ["Click", "Đăng ký", "Thanh toán"],
    datasets: [
      {
        label: "Số lượng",
        data: [
          stats.total_clicks || 0,
          stats.total_registrations || 0,
          stats.paid_customers || 0,
        ],
        backgroundColor: [
          "rgba(16, 185, 129, 0.8)", // Emerald
          "rgba(37, 99, 235, 0.8)", // Blue
          "rgba(245, 158, 11, 0.8)", // Amber
        ],
        borderWidth: 0,
      },
    ],
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem("affiliate_token");
        const res = await axios.get(`${END_POINT}/api/affiliate/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data && res.data.data) {
          setStats(res.data.data);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <AffiliateLayout title="Tổng quan">
      <div className="aff-dashboard-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-left">
            <h1>Xin chào, Đối tác! 👋</h1>
            <p>Đây là tổng quan về hiệu quả hoạt động của bạn hôm nay.</p>
          </div>
          <div className="header-right">
            <span className="update-time">Cập nhật: Vừa xong</span>
            <button
              onClick={() => window.location.reload()}
              className="refresh-btn"
              title="Tải lại trang"
            >
              <svg
                width="18"
                height="18"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="aff-page-content">
          {/* KPI Cards */}
          <div className="stats-grid">
            <KPI_Card
              title="Tổng Click"
              value={stats.total_clicks || 0}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              }
              variant="primary"
            />
            <KPI_Card
              title="Đăng ký mới"
              value={stats.total_registrations || 0}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
              }
              variant="blue"
            />
            <KPI_Card
              title="Khách mua gói"
              value={stats.paid_customers || 0}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
              variant="purple"
            />
            <KPI_Card
              title="Tổng hoa hồng"
              value={`${(stats.total_commission || 0).toLocaleString()} đ`}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              variant="success"
            />
            <KPI_Card
              title="Số dư khả dụng"
              value={`${(stats.available_balance || 0).toLocaleString()} đ`}
              icon={
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  />
                </svg>
              }
              variant="amber"
            />
          </div>

          {/* Charts Section */}
          <div className="content-grid">
            <div className="card-box chart-card">
              <div className="card-header-clean">
                <h3>Hiệu suất 7 ngày qua</h3>
              </div>
              <div className="chart-container">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>

            <div className="card-box chart-card">
              <div className="card-header-clean">
                <h3>Phễu chuyển đổi</h3>
              </div>
              <div className="chart-container">
                <Bar data={funnelData} options={funnelOptions} />
              </div>
            </div>
          </div>

          {/* Activity Log Section */}
          <div className="card-box activity-log-card">
            <div className="card-header-clean">
              <h3>Hoạt động gần đây</h3>
              <a href="/customers" className="view-all-link">
                Xem tất cả →
              </a>
            </div>
            <div className="activity-list">
              {stats.recent_activities && stats.recent_activities.length > 0 ? (
                stats.recent_activities.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div
                      className={`activity-icon ${activity.type || "default"}`}
                    >
                      {activity.type === "registration" ? (
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          />
                        </svg>
                      ) : activity.type === "payment" ? (
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      ) : (
                        <svg
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </div>
                    <div className="activity-content">
                      <p className="activity-text">{activity.message}</p>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                    {activity.amount && (
                      <div className="activity-amount">
                        +{activity.amount.toLocaleString()} đ
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="empty-activity">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    width="48"
                    height="48"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                  <p>Chưa có hoạt động nào</p>
                  <span>
                    Các hoạt động đăng ký, thanh toán sẽ hiển thị ở đây
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AffiliateLayout>
  );
};

export default AffiliateDashboard;
